import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  History, 
  User, 
  Calendar, 
  FileText, 
  Edit, 
  Trash2, 
  Eye, 
  Download,
  Archive,
  RotateCcw,
  ChevronDown,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icone per le azioni
const actionIcons = {
  create: <FileText className="w-4 h-4 text-green-500" />,
  update: <Edit className="w-4 h-4 text-blue-500" />,
  delete: <Trash2 className="w-4 h-4 text-red-500" />,
  view: <Eye className="w-4 h-4 text-slate-500" />,
  export: <Download className="w-4 h-4 text-purple-500" />,
  archive: <Archive className="w-4 h-4 text-amber-500" />,
  restore: <RotateCcw className="w-4 h-4 text-teal-500" />,
};

// Colori badge per azione
const actionColors = {
  create: "bg-green-100 text-green-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-red-100 text-red-700",
  view: "bg-slate-100 text-slate-700",
  export: "bg-purple-100 text-purple-700",
  archive: "bg-amber-100 text-amber-700",
  restore: "bg-teal-100 text-teal-700",
};

// Nomi italiani per sezioni
const sectionNames = {
  anagrafica: "Anagrafica",
  amministrativa: "Amministrativa",
  sociale: "Sociale",
  sanitaria: "Sanitaria",
  pai: "PAI",
  valutazioni: "Valutazioni",
  documenti: "Documenti",
  diario: "Diario",
  fascicolo: "Fascicolo",
  generale: "Generale",
};

// Nomi italiani per azioni
const actionNames = {
  create: "Creazione",
  update: "Modifica",
  delete: "Eliminazione",
  view: "Visualizzazione",
  export: "Export",
  archive: "Archiviazione",
  restore: "Ripristino",
};

export function AuditLogViewer({ fascicoloId, className = "" }) {
  const { token, hasPermission } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filterSection, setFilterSection] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [total, setTotal] = useState(0);

  // Verifica permesso audit:read
  const canViewAudit = hasPermission("audit:read");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSection !== "all") params.append("section", filterSection);
      if (filterAction !== "all") params.append("action", filterAction);
      params.append("limit", "50");

      const response = await axios.get(
        `${API_URL}/api/audit-logs/fascicolo/${fascicoloId}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Errore caricamento audit log:", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [fascicoloId, filterSection, filterAction, token]);

  useEffect(() => {
    if (canViewAudit && expanded && fascicoloId) {
      fetchLogs();
    }
  }, [expanded, filterSection, filterAction, fascicoloId, canViewAudit, fetchLogs]);

  // Se non ha permesso, non mostrare nulla
  if (!canViewAudit) {
    return null;
  }

  const formatDate = (isoString) => {
    if (!isoString) return "N/D";
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  return (
    <Card className={`${className}`}>
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="w-5 h-5 text-slate-600" />
            Storico Modifiche
            {total > 0 && (
              <Badge variant="outline" className="ml-2">
                {total} {total === 1 ? "evento" : "eventi"}
              </Badge>
            )}
          </CardTitle>
          <ChevronDown 
            className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} 
          />
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {/* Filtri */}
          <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Filtri:</span>
            </div>
            
            <Select value={filterSection} onValueChange={setFilterSection}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue placeholder="Sezione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sezioni</SelectItem>
                {Object.entries(sectionNames).map(([key, name]) => (
                  <SelectItem key={key} value={key}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Azione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le azioni</SelectItem>
                {Object.entries(actionNames).map(([key, name]) => (
                  <SelectItem key={key} value={key}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchLogs}
              disabled={loading}
            >
              Aggiorna
            </Button>
          </div>

          {/* Lista Log */}
          {loading ? (
            <div className="text-center py-8 text-slate-500">
              Caricamento...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Nessun log trovato
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div 
                  key={log._id || index}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  {/* Icona azione */}
                  <div className="mt-0.5">
                    {actionIcons[log.action] || <FileText className="w-4 h-4" />}
                  </div>

                  {/* Contenuto */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge className={`text-xs ${actionColors[log.action] || "bg-slate-100"}`}>
                        {actionNames[log.action] || log.action}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {sectionNames[log.section] || log.section}
                      </Badge>
                    </div>
                    
                    {/* Campi modificati */}
                    {log.changed_fields && log.changed_fields.length > 0 && (
                      <p className="text-sm text-slate-600 mb-1">
                        Campi: {log.changed_fields.join(", ")}
                      </p>
                    )}

                    {/* Utente e data */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.userName || log.userId}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(log.timestamp)}
                      </span>
                      {log.ip && (
                        <span className="text-slate-400">
                          IP: {log.ip}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default AuditLogViewer;
